import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Input, Modal, Skeleton, Tag, message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import {
  SAVE_WORKBENCH_DAILY_REPORT_DRAFT,
  SAVE_WORKBENCH_DAILY_REPORT_RISK_COMMENT,
} from '../graphql/mutations/workbenchMutations';
import { severityOf } from '../utils/alertTextComposer';
import { byHandledAtDesc } from '../utils/riskSort';
import { formatLocalDateTime, formatLocalTime } from '../utils/taskTextComposer';

interface ShiftReportSummary {
  tasksDone: number;
  tasksTotal: number;
  tasksStillOpen: number;
  alertsHandled: number;
  alertsTotal: number;
  returnParcelsDone: number;
  returnParcelsTotal: number;
}

/** KPI 芯片的字段后端均为非空（见 WorkbenchMetricType），故此处不标可空 */
interface ShiftReportMetric {
  metricKey: string;
  valueText: string;
  dodChangeText?: string | null;
  severity: string;
}

/**
 * 日报里的风险条目。
 *
 * 不复用 `WorkbenchAlert`：日报的 `DailyReportRiskDTO` 远薄于告警，
 * `entities` / `trend` / `actions` 等一律为空（plan 3-6），
 * 用胖类型会让人误以为这些字段有值。此处只声明查询真正选取的字段。
 */
interface ShiftReportRisk {
  /** 上游 risks 的 taskId，或草稿 handledRecords 的 taskId；无 id 的条目后端已丢弃 */
  alertId: number;
  /** 「已处理」记录带 `subType`；上游 risks 无此字段，恒为空 */
  alertTypeCode?: string | null;
  severity?: string | null;
  headline?: string | null;
  handleStatus?: string | null;
  /** 处置人与处置时刻。动作类处置（如「联系 DSP」）没有留言，只有这两个字段 */
  handledByName?: string | null;
  handledAt?: string | null;
  comment?: {
    content?: string | null;
    byName?: string | null;
    at?: string | null;
    canEdit?: boolean | null;
  } | null;
}

interface ShiftReportSectionProps {
  data: {
    available: boolean;
    unavailableReason?: string | null;
    reportId?: number | null;
    reportDate: string;
    status?: string | null;
    autoSendAtText?: string | null;
    sentAt?: string | null;
    lastEditedAt?: string | null;
    canEdit?: boolean | null;
    summary?: ShiftReportSummary | null;
    kpis: ShiftReportMetric[];
    risks: ShiftReportRisk[];
    unresolvedPeriodRisks: ShiftReportRisk[];
    unhandledAlertCount?: number | null;
    remark?: string | null;
  };
  warehouseId?: number;
  timeZone?: string | null;
  loading?: boolean;
  onDraftSaved?: () => void;
  /**
   * 「已处理风险」按当前显示的告警栏合计：
   * - 传对象 → 用对象里的 handled/total/unhandled 出数（父层已按开关挑好显示栏合计）
   * - 传 `null` → 两栏都被 Nacos 关掉，整格不渲染
   * - 未传 → 回落到 `summary.alertsHandled / alertsTotal`（老口径，父层未接入前用）
   *
   * 之所以不直接读 `kpi.riskTotal / riskHandled`：上游那对字段是硬合并
   * （实时 + 周期），无视开关。仓管关掉周期栏后，「已处理风险」仍把周期数塞进分母
   * 会让仓管对不上页面上真正显示的那几栏
   */
  alertsCountOverride?: { handled: number; total: number; unhandled: number } | null;
}

// 配色档位与实时告警同源：上游 severity 恒为空，按告警类型派生（领取率红、妥投率黄）
const severityDot = (risk: ShiftReportRisk): string => {
  switch (severityOf(risk.severity, risk.alertTypeCode)) {
    case 'HIGH':
      return 'bg-red-500';
    case 'MEDIUM':
      return 'bg-amber-500';
    default:
      return 'bg-orange-300';
  }
};

/**
 * 四档染色，分档与 KPI 条、「历史包裹追踪」页一致。
 * 用 antd Tag 预设色而非 Tailwind 色值，WARNING 取 gold —— 预设里没有和 yellow-500 等值的档，
 * 而 yellow 标签底色过浅、文字读不清。
 */
const METRIC_TAG_COLOR: Record<string, string> = {
  EXCELLENT: 'green',
  GOOD: 'blue',
  WARNING: 'gold',
  CRITICAL: 'red',
};

const metricTagColor = (severity: string): string => METRIC_TAG_COLOR[severity] || 'default';

const ShiftReportSection: React.FC<ShiftReportSectionProps> = ({
  data,
  warehouseId,
  timeZone,
  loading,
  onDraftSaved,
  alertsCountOverride,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [saveDraft, { loading: saving }] = useMutation(SAVE_WORKBENCH_DAILY_REPORT_DRAFT);

  const [editingRisk, setEditingRisk] = React.useState<ShiftReportRisk | null>(null);
  const [riskComment, setRiskComment] = React.useState('');
  const [saveRiskComment, { loading: savingRiskComment }] =
    useMutation(SAVE_WORKBENCH_DAILY_REPORT_RISK_COMMENT);

  // 未处理的周期复盘风险排最上面（open 无处理时刻，走原顺序），
  // 已处理的实时预警 + 周期复盘按 handledAt 倒序追加
  const sortedRisks = React.useMemo(
    () => [
      ...data.unresolvedPeriodRisks,
      ...[...data.risks].sort(byHandledAtDesc),
    ],
    [data.risks, data.unresolvedPeriodRisks]
  );

  const openEditor = () => {
    // 打开时载入已存内容，避免仓管以为之前写的丢了
    setDraft(data.remark ?? '');
    setEditing(true);
  };

  const openRiskEditor = (risk: ShiftReportRisk) => {
    setRiskComment(risk.comment?.content ?? '');
    setEditingRisk(risk);
  };

  const handleSaveRiskComment = async () => {
    const content = riskComment.trim();
    // reportId 为空时透传给上游，由上游按仓 + 业务日兜底
    if (!warehouseId || !editingRisk || !content) return;
    try {
      await saveRiskComment({
        variables: {
          warehouseId,
          reportId: data.reportId,
          alertId: editingRisk.alertId,
          content,
        },
      });
      message.success(String(t('workbench.commentSaved')));
      setEditingRisk(null);
      onDraftSaved?.();
    } catch (e) {
      message.error(String(t('workbench.alertText.handleFailed')));
    }
  };

  const handleSaveDraft = async () => {
    // reportId 为空时透传给上游，由上游按仓 + 业务日兜底新建
    if (!warehouseId) return;
    try {
      await saveDraft({
        variables: { warehouseId, reportId: data.reportId, remark: draft },
      });
      message.success(String(t('workbench.report.draftSaved')));
      setEditing(false);
      onDraftSaved?.();
    } catch (e) {
      message.error(String(t('workbench.alertText.handleFailed')));
    }
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  const { summary } = data;
  // 自动送达时刻取上游编辑窗口右端（按仓 nacos 配置）；上游没给时区算不出，回落到 PRD 默认的 21:00
  const autoSendAt = data.autoSendAtText || String(t('workbench.report.autoSendAtFallback'));
  const parsedDate = dayjs(data.reportDate);
  const weekdays = t('workbench.weekdays', { returnObjects: true }) as unknown as string[];
  const weekdayText = Array.isArray(weekdays) ? weekdays[parsedDate.day()] : '';
  const dateText = parsedDate.isValid()
    ? parsedDate.format(String(t('workbench.dateFormat')))
    : data.reportDate;

  const metricLabels: Record<string, string> = {
    PICKING_RATE: String(t('workbench.metrics.pickingRate')),
    DELIVERY_RATE: String(t('workbench.metrics.deliveryRate')),
    RETURN_RATE: String(t('workbench.metrics.returnRate')),
    NO_UPDATE: String(t('workbench.metrics.noUpdateShort')),
  };

  const tasksPercent =
    summary && summary.tasksTotal > 0
      ? Math.round((summary.tasksDone / summary.tasksTotal) * 100)
      : 0;

  const draftModal = (
    <Modal
      open={editing}
      title={String(t('workbench.report.editDraft'))}
      onCancel={() => setEditing(false)}
      onOk={handleSaveDraft}
      confirmLoading={saving}
      okText={String(t('components.button.save'))}
    >
      <Input.TextArea
        rows={5}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={String(t('workbench.report.remarkPlaceholder'))}
      />
    </Modal>
  );

  const riskCommentModal = (
    <Modal
      open={editingRisk !== null}
      title={String(t('workbench.report.editRiskComment'))}
      onCancel={() => setEditingRisk(null)}
      onOk={handleSaveRiskComment}
      confirmLoading={savingRiskComment}
      okText={String(t('components.button.save'))}
      okButtonProps={{ disabled: !riskComment.trim() }}
      destroyOnClose
    >
      <p className="text-sm text-gray-600 mb-2">{editingRisk?.headline}</p>
      <Input.TextArea
        rows={4}
        value={riskComment}
        onChange={(e) => setRiskComment(e.target.value)}
        placeholder={String(t('workbench.addCommentPlaceholder'))}
      />
    </Modal>
  );

  return (
    <div className="border border-violet-200 rounded-lg">
      {draftModal}
      {riskCommentModal}
      {/* 头部 */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-violet-200">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h3 className="text-base font-semibold text-violet-600">
            {String(t('workbench.report.title', {
              date: `${dateText} ${weekdayText}`,
            }))}
          </h3>
          {autoSendAt && data.status !== 'SENT' && (
            <Tag color="purple" className="m-0">
              {String(t('workbench.report.autoSendTag', { time: autoSendAt }))}
            </Tag>
          )}
          {data.status === 'SENT' && (
            <Tag color="green" className="m-0">
              {String(t('workbench.report.sent'))}
            </Tag>
          )}
          <span className="text-xs text-gray-500">
            {String(t('workbench.report.autoGeneratedHint', { time: autoSendAt }))}
          </span>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {data.lastEditedAt && (
            <span className="text-xs text-gray-500">
              {String(t('workbench.report.lastEdited'))} {formatLocalTime(data.lastEditedAt, timeZone) || '—'}
            </span>
          )}
          {data.canEdit && (
            <Button size="small" className="text-violet-600" onClick={openEditor}>
              {String(t('workbench.report.editDraft'))}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 divide-y xl:divide-y-0 xl:divide-x divide-violet-200">
        {/* 左：今日完成情况 */}
        <div className="p-4">
          <p className="text-sm font-medium text-gray-700">
            {String(t('workbench.report.todayProgress'))}
          </p>

          {summary ? (
            <>
              <div className="grid grid-cols-4 gap-4 mt-3">
                <div>
                  <p className="text-xs text-gray-500">{String(t('workbench.report.tasksDone'))}</p>
                  <p className="text-lg font-bold">
                    <span className="text-emerald-600">{summary.tasksDone}</span>
                    <span className="text-gray-400 text-sm"> / {summary.tasksTotal}</span>
                  </p>
                  <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden mt-1">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{ width: `${tasksPercent}%` }}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500">{String(t('workbench.report.stillOpen'))}</p>
                  <p className="text-lg font-bold text-gray-900">{summary.tasksStillOpen}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {String(t('workbench.report.stillOpenHint'))}
                  </p>
                </div>

                {/* override === null 表示两栏都被 Nacos 关掉，整格不出——0/0 也是误导，仓管会以为「今天没风险」 */}
                {alertsCountOverride !== null && (() => {
                  const alertsHandled = alertsCountOverride?.handled ?? summary.alertsHandled;
                  const alertsTotal = alertsCountOverride?.total ?? summary.alertsTotal;
                  const unhandled = alertsCountOverride
                    ? alertsCountOverride.unhandled
                    : (data.unhandledAlertCount ?? 0);
                  return (
                    <div>
                      <p className="text-xs text-gray-500">
                        {String(t('workbench.report.alertsHandled'))}
                      </p>
                      <p className="text-lg font-bold">
                        <span className="text-gray-900">{alertsHandled}</span>
                        <span className="text-gray-400 text-sm"> / {alertsTotal}</span>
                      </p>
                      {/* 后端 kpi 缺失时 unhandled 为 0；"没算出来"不等于"0 条未处理"，故 0 不展示 */}
                      {unhandled > 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          {String(t('workbench.report.unhandledCount', { count: unhandled }))}
                        </p>
                      )}
                    </div>
                  );
                })()}

                <div>
                  <p className="text-xs text-gray-500">
                    {String(t('workbench.report.returnParcels'))}
                  </p>
                  <p className="text-lg font-bold">
                    <span className="text-gray-900">{summary.returnParcelsDone}</span>
                    <span className="text-gray-400 text-sm"> / {summary.returnParcelsTotal}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {String(t('workbench.report.pendingPutaway', {
                      count: summary.returnParcelsTotal - summary.returnParcelsDone,
                    }))}
                  </p>
                </div>
              </div>

              {data.kpis.length > 0 && (
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <span className="text-xs text-gray-500">
                    {String(t('workbench.report.dayMetrics'))}
                  </span>
                  {data.kpis.map((kpi) => (
                    // 环比暂时下线：数仓 SLA 表按日聚合，同窗口对比要等小时切片
                    <Tag key={kpi.metricKey} color={metricTagColor(kpi.severity)} className="m-0">
                      {metricLabels[kpi.metricKey] || kpi.metricKey} {kpi.valueText}
                    </Tag>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Empty className="my-4" description={String(t('workbench.report.noSummary'))} />
          )}
        </div>

        {/* 右：已处理的风险 */}
        <div className="p-4">
          <p className="text-sm font-medium text-gray-700">
            {String(t('workbench.report.risksToFlag'))}
          </p>

          {/* 风险条数不可控（每类告警一条，还会带留言），不封顶会把日报整块越撑越高，
              把下面的三栏挤出首屏。高度对齐左栏，超出部分列表内滚。
              补充说明虽非风险条目，但要与风险一起滚 —— 钉在滚动区外会遮住第一条风险 */}
          <div className="mt-3 space-y-4 max-h-52 overflow-y-auto pr-1">
            {data.remark?.trim() && (
              <div className="border-l-2 border-violet-200 pl-3">
                <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
                  {data.remark}
                </p>
              </div>
            )}

            {sortedRisks.length > 0 ? (
              sortedRisks.map((risk) => {
                // 处置信息优先取留言（留言可改，是最新的），无留言时回落到处置动作本身带的处理人/时刻 ——
                // 「联系 DSP」这类动作不产生留言，此前整行元信息都不渲染，看不出谁在何时处理的
                const handledBy = risk.comment?.byName ?? risk.handledByName;
                const handledAt = risk.comment?.at ?? risk.handledAt;
                const canEditComment = (risk.comment?.canEdit ?? true) && data.canEdit;

                return (
                <div key={risk.alertId}>
                  <div className="flex items-start gap-2">
                    <span
                      className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${severityDot(risk)}`}
                    />
                    <p className="text-sm text-gray-800 flex-1 min-w-0">{risk.headline}</p>
                    {risk.handleStatus !== 'OPEN' && (
                      <Tag color="green" className="shrink-0 m-0">
                        {String(t('workbench.report.handled'))}
                      </Tag>
                    )}
                  </div>

                  {(risk.comment?.content || handledBy || handledAt) && (
                    <div className="ml-4 mt-1.5 border-l-2 border-violet-200 pl-3">
                      {risk.comment?.content && (
                        <p className="text-sm text-gray-600 italic">“{risk.comment.content}”</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {handledBy} {handledAt ? (formatLocalDateTime(handledAt, timeZone) || '—') : ''}
                        {canEditComment && (
                          <>
                            {' · '}
                            <button
                              type="button"
                              className="text-violet-600 hover:underline"
                              onClick={() => openRiskEditor(risk)}
                            >
                              {String(t('workbench.edit'))}
                            </button>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                );
              })
            ) : (
              <Empty className="my-4" description={String(t('workbench.report.noRisks'))} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftReportSection;
